import { graphql, fetchQuery } from 'react-relay'
import { commitLocalUpdate } from 'relay-runtime'
import type { IEnvironment } from 'relay-runtime'

import { startDownload, cancelDownload } from '../Daemon'
import type { DownloadProgress } from '../Daemon'

import debug from 'debug'
const log = debug('pipeline:downloader')

interface VideoRef {
  URL: string
  SHA256: string
}

const query = graphql`
  query DownloaderQuery($orderID: ID!) {
    videoURL(orderID: $orderID) {
      URL,
      SHA256,
    }
  }`

let currentlyPreloading = new Map()

export function currentlyPreloadingCount(): number {
  return currentlyPreloading.size
}

export async function cancelPreload(environment: IEnvironment, orderId: string) {
  console.log("Cancelling preload of " + orderId)

  commitLocalUpdate(environment, store => {
    const order = store.get(orderId)
    if (!order) return
    order.setValue(null, 'videoDownloadStatus')
    order.setValue(null, 'videoTransferStatus')

    const sha = order.getValue('cardVideoSHA256')
    if (sha){
      currentlyPreloading.delete(sha)
      cancelDownload(sha as string)
    }
  })
}

export async function cancelAllPreloads(environment: IEnvironment) {
  commitLocalUpdate(environment, store => {
    let orders = store.getRoot().getLinkedRecords('orders')
    if (!orders){
      return
    }

    orders.forEach((order) => {
      if (!order) {
        return
      }

      order.setValue(null, 'videoDownloadStatus')
      order.setValue(null, 'videoTransferStatus')

      const sha = order.getValue('cardVideoSHA256')
      if (sha){
        currentlyPreloading.delete(sha as string)
        cancelDownload(sha as string)
      }
    })
  })
}

async function startPreload(environment: IEnvironment, dataID: string, video: VideoRef){
  if (currentlyPreloading.has(video.SHA256)) {
    return
  }

  currentlyPreloading.set(video.SHA256, dataID)

  const handleError = (message: string) => {
    console.error("Error preloading:", message)
    currentlyPreloading.delete(video.SHA256)
  }

  const handleSuccess = () => {
    currentlyPreloading.delete(video.SHA256)
  }

  const res = startDownload(video.URL, video.SHA256, (p: DownloadProgress) => {
    commitLocalUpdate(environment, store => {
      let order = store.get(dataID)
      order && order.setValue(p.percent_complete, 'videoPercentageDownloaded')
    })
  })

  res.then(() => {
    commitLocalUpdate(environment, store => {
      let order = store.get(dataID)
      order && order.setValue('downloaded', 'videoDownloadStatus')
    })

    handleSuccess()
  }, (err) => {
    commitLocalUpdate(environment, store => {
      let order = store.get(dataID)
      order && order.setValue('error', 'videoDownloadStatus')
    })

    handleError(err.message)
  })
}

export interface AlbumVideoInfo {
  pageNumber: number
  sha256: string
  url: string
}

// Extract album video info from an order's designs.destinations.albumDesign.pages.
// Uses the normalize render output (cardVideoURL/SHA256) for device-ready video.
export function getAlbumVideos(order: any): AlbumVideoInfo[] {
  const videos: AlbumVideoInfo[] = []
  if (!order?.designs) return videos
  for (const dwd of order.designs) {
    if (!dwd?.destinations) continue
    for (const dest of dwd.destinations) {
      if (!dest?.albumDesign?.pages) continue
      for (const page of dest.albumDesign.pages) {
        if (page.cardVideoURL && page.cardVideoSHA256) {
          videos.push({
            pageNumber: page.pageNumber,
            sha256: page.cardVideoSHA256,
            url: page.cardVideoURL,
          })
        }
      }
    }
  }
  return videos
}

// Downloads all videos for an album order. Uses the mediaId as cache key
// since we don't have a pre-computed SHA256 for encoded media files.
export async function preloadAlbumVideos(environment: IEnvironment, orderId: string, videos: AlbumVideoInfo[]) {
  if (preloadPending.has(orderId)) return
  preloadPending.add(orderId)

  commitLocalUpdate(environment, store => {
    let order = store.get(orderId)
    order && order.setValue('downloading', 'videoDownloadStatus')
  })

  let completed = 0
  let failed = false

  for (const video of videos) {
    if (currentlyPreloading.has(video.sha256)) continue

    try {
      currentlyPreloading.set(video.sha256, orderId)

      await startDownload(video.url, video.sha256, (p: DownloadProgress) => {
        commitLocalUpdate(environment, store => {
          let order = store.get(orderId)
          if (order) {
            const overallPercent = ((completed + p.percent_complete / 100) / videos.length) * 100
            order.setValue(overallPercent, 'videoPercentageDownloaded')
          }
        })
      })

      completed++
      currentlyPreloading.delete(video.sha256)
    } catch (err) {
      currentlyPreloading.delete(video.sha256)
      failed = true
      break
    }
  }

  preloadPending.delete(orderId)

  commitLocalUpdate(environment, store => {
    let order = store.get(orderId)
    if (order) {
      order.setValue(failed ? 'error' : 'downloaded', 'videoDownloadStatus')
    }
  })
}

let preloadPending: Set<string> = new Set()
export function preloadVideo(environment: IEnvironment, orderId: string){
  if (preloadPending.has(orderId)){
    return
  }

  log('Preload start')

  commitLocalUpdate(environment, store => {
    const order = store.get(orderId)
    if (!order){
      return
    }
    preloadPending.add(orderId)

    log('Preload query fetch', orderId)

    fetchQuery(environment as any, query as any, {orderID: orderId})
      .subscribe({
        next: (resp: any) => {
          preloadPending.delete(orderId)

          if (resp && resp.videoURL){
            const dataID = order!.getDataID()
            if (!dataID)
              return

            startPreload(environment, dataID, resp.videoURL)

            commitLocalUpdate(environment, store => {
              let order = store.get(orderId)
              order && order.setValue('downloading', 'videoDownloadStatus')
            })
          }
        },
      error: () => {
        preloadPending.delete(orderId)
      }
    })
  })
}

